import React, { useState, useRef, useCallback, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { RotateCw, Trash2, ZoomIn, ZoomOut, Eye, Maximize2, PenLine, MousePointer, Ruler, Plus } from "lucide-react";

// ============== PIECE CATALOG ==============
const KITCHEN_PIECES = [
  { id: "base_12", cat: "Base", label: 'Base 12"', w: 12, h: 24, color: "#8b5cf6" },
  { id: "base_15", cat: "Base", label: 'Base 15"', w: 15, h: 24, color: "#8b5cf6" },
  { id: "base_18", cat: "Base", label: 'Base 18"', w: 18, h: 24, color: "#8b5cf6" },
  { id: "base_24", cat: "Base", label: 'Base 24"', w: 24, h: 24, color: "#8b5cf6" },
  { id: "base_30", cat: "Base", label: 'Base 30"', w: 30, h: 24, color: "#8b5cf6" },
  { id: "base_36", cat: "Base", label: 'Base 36"', w: 36, h: 24, color: "#8b5cf6" },
  { id: "wall_12", cat: "Wall", label: 'Wall 12"', w: 12, h: 12, color: "#3b82f6" },
  { id: "wall_15", cat: "Wall", label: 'Wall 15"', w: 15, h: 12, color: "#3b82f6" },
  { id: "wall_18", cat: "Wall", label: 'Wall 18"', w: 18, h: 12, color: "#3b82f6" },
  { id: "wall_24", cat: "Wall", label: 'Wall 24"', w: 24, h: 12, color: "#3b82f6" },
  { id: "wall_30", cat: "Wall", label: 'Wall 30"', w: 30, h: 12, color: "#3b82f6" },
  { id: "wall_36", cat: "Wall", label: 'Wall 36"', w: 36, h: 12, color: "#3b82f6" },
  { id: "tall_24", cat: "Tall", label: 'Tall 24"', w: 24, h: 24, color: "#6366f1" },
  { id: "tall_30", cat: "Tall", label: 'Tall 30"', w: 30, h: 24, color: "#6366f1" },
  { id: "tall_36", cat: "Tall", label: 'Tall 36"', w: 36, h: 24, color: "#6366f1" },
  { id: "corner_base", cat: "Corner", label: "Corner Base", w: 36, h: 36, color: "#a855f7" },
  { id: "corner_wall", cat: "Corner", label: "Corner Wall", w: 24, h: 24, color: "#a855f7" },
  { id: "lazy_susan", cat: "Corner", label: "Lazy Susan", w: 33, h: 33, color: "#a855f7" },
  { id: "island", cat: "Island", label: "Island", w: 60, h: 36, color: "#14b8a6" },
  { id: "pantry", cat: "Tall", label: "Pantry", w: 36, h: 24, color: "#6366f1" },
  { id: "open_shelf", cat: "Wall", label: "Open Shelf", w: 30, h: 8, color: "#3b82f6" },
  { id: "filler", cat: "Other", label: "Filler", w: 3, h: 24, color: "#94a3b8" },
  { id: "sink", cat: "Appliance", label: "Sink", w: 33, h: 22, color: "#0ea5e9" },
  { id: "stove", cat: "Appliance", label: "Stove/Range", w: 30, h: 26, color: "#ef4444" },
  { id: "fridge", cat: "Appliance", label: "Fridge", w: 36, h: 30, color: "#64748b" },
  { id: "dishwasher", cat: "Appliance", label: "Dishwasher", w: 24, h: 24, color: "#22c55e" },
  { id: "microwave_cab", cat: "Wall", label: "Microwave Cab", w: 30, h: 18, color: "#3b82f6" },
  { id: "hood_range", cat: "Wall", label: "Hood Range", w: 30, h: 12, color: "#f59e0b" },
];

const BATHROOM_PIECES = [
  { id: "vanity_24", cat: "Vanity", label: 'Vanity 24"', w: 24, h: 20, color: "#8b5cf6" },
  { id: "vanity_30", cat: "Vanity", label: 'Vanity 30"', w: 30, h: 20, color: "#8b5cf6" },
  { id: "vanity_36", cat: "Vanity", label: 'Vanity 36"', w: 36, h: 20, color: "#8b5cf6" },
  { id: "vanity_48", cat: "Vanity", label: 'Vanity 48"', w: 48, h: 22, color: "#8b5cf6" },
  { id: "vanity_60", cat: "Vanity", label: 'Vanity 60"', w: 60, h: 22, color: "#8b5cf6" },
  { id: "toilet", cat: "Fixture", label: "Toilet", w: 18, h: 28, color: "#e2e8f0" },
  { id: "bathtub", cat: "Fixture", label: "Bathtub", w: 60, h: 32, color: "#bae6fd" },
  { id: "shower_36", cat: "Fixture", label: 'Shower 36"', w: 36, h: 36, color: "#bae6fd" },
  { id: "shower_48", cat: "Fixture", label: 'Shower 48"', w: 48, h: 36, color: "#bae6fd" },
  { id: "curbless_shower", cat: "Fixture", label: "Curbless Shower", w: 48, h: 48, color: "#7dd3fc" },
  { id: "shower_niche_s", cat: "Niche", label: 'Niche 12"', w: 12, h: 4, color: "#fbbf24" },
  { id: "shower_niche_l", cat: "Niche", label: 'Niche 24"', w: 24, h: 4, color: "#fbbf24" },
  { id: "led_mirror_24", cat: "Lighting", label: 'LED Mirror 24"', w: 24, h: 2, color: "#facc15" },
  { id: "led_mirror_36", cat: "Lighting", label: 'LED Mirror 36"', w: 36, h: 2, color: "#facc15" },
  { id: "led_accent", cat: "Lighting", label: "LED Accent Strip", w: 24, h: 2, color: "#fde68a" },
  { id: "led_under_vanity", cat: "Lighting", label: "Under Vanity LED", w: 30, h: 2, color: "#fde68a" },
  { id: "led_niche_light", cat: "Lighting", label: "Niche LED Light", w: 12, h: 2, color: "#fde68a" },
  { id: "linen_cabinet", cat: "Storage", label: "Linen Cabinet", w: 18, h: 20, color: "#6366f1" },
];

const S = 3; // scale: pixels per inch
const WALL_THICKNESS = 6; // inches
const SNAP_DISTANCE = 6; // snap within 6 inches of a wall

export function KitchenDesigner({ value, onChange }) {
  const canvasRef = useRef(null);
  const [pieces, setPieces] = useState(value?.pieces || []);
  const [walls, setWalls] = useState(value?.walls || []);
  const [roomType, setRoomType] = useState(value?.roomType || "kitchen");
  const [viewMode, setViewMode] = useState("floor");
  const [tool, setTool] = useState("select"); // select, wall
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [zoom, setZoom] = useState(100);
  const [catFilter, setCatFilter] = useState("all");
  const [customLabel, setCustomLabel] = useState("");
  const [drawingWall, setDrawingWall] = useState(null); // {x1,y1} while drawing
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [showMeasurements, setShowMeasurements] = useState(true);
  const dragOffset = useRef({ x: 0, y: 0 });

  const catalog = roomType === "kitchen" ? KITCHEN_PIECES : BATHROOM_PIECES;
  const categories = [...new Set(catalog.map(p => p.cat))];
  const filteredCatalog = catFilter === "all" ? catalog : catalog.filter(p => p.cat === catFilter);

  // Auto-save
  useEffect(() => {
    const t = setTimeout(() => onChange?.({ pieces, walls, roomType, viewMode }), 500);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pieces, walls]); // eslint-disable-line react-hooks/exhaustive-deps

  // Snap piece to nearest wall
  const snapToWall = (px, py, pw, ph) => {
    let snappedX = px, snappedY = py;
    let snapped = false;
    for (const wall of walls) {
      // Horizontal wall
      if (Math.abs(wall.y1 - wall.y2) < 2) {
        const wy = wall.y1;
        // Snap top edge to wall
        if (Math.abs(py - wy) < SNAP_DISTANCE) { snappedY = wy; snapped = true; }
        // Snap bottom edge to wall
        if (Math.abs((py + ph) - wy) < SNAP_DISTANCE) { snappedY = wy - ph; snapped = true; }
      }
      // Vertical wall
      if (Math.abs(wall.x1 - wall.x2) < 2) {
        const wx = wall.x1;
        if (Math.abs(px - wx) < SNAP_DISTANCE) { snappedX = wx; snapped = true; }
        if (Math.abs((px + pw) - wx) < SNAP_DISTANCE) { snappedX = wx - pw; snapped = true; }
      }
    }
    return { x: snappedX, y: snappedY, snapped };
  };

  const addPiece = (template) => {
    const p = {
      uid: `p_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      templateId: template.id, label: template.label,
      x: 40 + Math.random() * 60, y: 40 + Math.random() * 60,
      w: template.w, h: template.h, color: template.color, rotation: 0, cat: template.cat,
    };
    setPieces([...pieces, p]);
    setSelectedPiece(p.uid);
    setTool("select");
  };

  const addCustomPiece = () => {
    if (!customLabel.trim()) return;
    addPiece({ id: "custom", cat: "Custom", label: customLabel.trim(), w: 24, h: 24, color: "#78716c" });
    setCustomLabel("");
  };

  const updatePiece = (uid, updates) => setPieces(pieces.map(p => p.uid === uid ? { ...p, ...updates } : p));
  const deletePiece = (uid) => { setPieces(pieces.filter(p => p.uid !== uid)); if (selectedPiece === uid) setSelectedPiece(null); };
  const rotatePiece = (uid) => { const p = pieces.find(pp => pp.uid === uid); if (p) updatePiece(uid, { rotation: (p.rotation + 90) % 360, w: p.h, h: p.w }); };
  const deleteWall = (idx) => setWalls(walls.filter((_, i) => i !== idx));

  const getCanvasPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const z = zoom / 100;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) / z / S, y: (clientY - rect.top) / z / S };
  };

  // Mouse/touch handlers
  const handleCanvasMouseDown = (e) => {
    const pos = getCanvasPos(e);
    if (tool === "wall") {
      setDrawingWall({ x1: Math.round(pos.x), y1: Math.round(pos.y) });
      return;
    }
    setSelectedPiece(null);
  };

  const handleCanvasMouseMove = (e) => {
    const pos = getCanvasPos(e);
    setMousePos(pos);
    if (dragging) {
      const nx = pos.x - dragOffset.current.x;
      const ny = pos.y - dragOffset.current.y;
      const piece = pieces.find(p => p.uid === dragging);
      if (piece) {
        const { x, y } = snapToWall(nx, ny, piece.w, piece.h);
        updatePiece(dragging, { x: Math.max(0, x), y: Math.max(0, y) });
      }
    }
    if (resizing) {
      const piece = pieces.find(p => p.uid === resizing);
      if (piece) {
        updatePiece(resizing, { w: Math.max(6, Math.round(pos.x - piece.x)), h: Math.max(6, Math.round(pos.y - piece.y)) });
      }
    }
  };

  const handleCanvasMouseUp = (e) => {
    if (tool === "wall" && drawingWall) {
      const pos = getCanvasPos(e.changedTouches ? e.changedTouches[0] : e);
      const x2 = Math.round(pos.x), y2 = Math.round(pos.y);
      const dx = Math.abs(x2 - drawingWall.x1), dy = Math.abs(y2 - drawingWall.y1);
      if (dx > 6 || dy > 6) {
        // Snap to horizontal or vertical
        let finalX2 = x2, finalY2 = y2;
        if (dx > dy) finalY2 = drawingWall.y1; // horizontal
        else finalX2 = drawingWall.x1; // vertical
        setWalls([...walls, { x1: drawingWall.x1, y1: drawingWall.y1, x2: finalX2, y2: finalY2 }]);
      }
      setDrawingWall(null);
      return;
    }
    setDragging(null);
    setResizing(null);
  };

  const handlePieceMouseDown = (e, uid) => {
    if (tool !== "select") return;
    e.stopPropagation();
    const pos = getCanvasPos(e);
    const piece = pieces.find(p => p.uid === uid);
    if (!piece) return;
    setSelectedPiece(uid);
    dragOffset.current = { x: pos.x - piece.x, y: pos.y - piece.y };
    setDragging(uid);
  };

  const wallLength = (w) => {
    const dx = w.x2 - w.x1, dy = w.y2 - w.y1;
    return Math.round(Math.sqrt(dx * dx + dy * dy));
  };

  const formatInches = (inches) => {
    const ft = Math.floor(inches / 12);
    const inn = inches % 12;
    return ft > 0 ? `${ft}'${inn > 0 ? `-${inn}"` : ""}` : `${inn}"`;
  };

  const gridW = 240, gridH = 180;

  return (
    <div className="space-y-3" data-testid="kitchen-designer">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex border rounded-lg p-1 gap-1">
          <button className={`px-3 py-1.5 rounded text-xs font-medium ${roomType === "kitchen" ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`} onClick={() => { setRoomType("kitchen"); setCatFilter("all"); }}>Kitchen</button>
          <button className={`px-3 py-1.5 rounded text-xs font-medium ${roomType === "bathroom" ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`} onClick={() => { setRoomType("bathroom"); setCatFilter("all"); }}>Bathroom</button>
        </div>
        <div className="h-5 w-px bg-slate-300" />
        <div className="flex border rounded-lg p-1 gap-1">
          <button className={`px-2 py-1.5 rounded text-xs font-medium flex items-center gap-1 ${tool === "select" ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-100"}`} onClick={() => setTool("select")} data-testid="tool-select">
            <MousePointer className="w-3 h-3" /> Select
          </button>
          <button className={`px-2 py-1.5 rounded text-xs font-medium flex items-center gap-1 ${tool === "wall" ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-100"}`} onClick={() => { setTool("wall"); setSelectedPiece(null); }} data-testid="tool-wall">
            <PenLine className="w-3 h-3" /> Draw Wall
          </button>
        </div>
        <div className="h-5 w-px bg-slate-300" />
        <button className={`px-2 py-1 rounded text-xs ${showMeasurements ? "bg-blue-100 text-blue-700" : "text-slate-500"}`} onClick={() => setShowMeasurements(!showMeasurements)}>
          <Ruler className="w-3 h-3 inline mr-1" />Dims
        </button>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-7" onClick={() => setZoom(Math.max(50, zoom - 10))}><ZoomOut className="w-3 h-3" /></Button>
          <span className="text-xs text-slate-500 w-10 text-center">{zoom}%</span>
          <Button variant="ghost" size="sm" className="h-7" onClick={() => setZoom(Math.min(150, zoom + 10))}><ZoomIn className="w-3 h-3" /></Button>
        </div>
        <span className="text-xs text-slate-400 ml-auto">{pieces.length} pieces, {walls.length} walls</span>
      </div>

      <div className="flex gap-4">
        {/* Piece Palette */}
        <div className="w-48 shrink-0 space-y-3">
          <div className="flex flex-wrap gap-1">
            <button className={`px-2 py-0.5 rounded text-xs ${catFilter === "all" ? "bg-slate-800 text-white" : "text-slate-500 hover:bg-slate-100"}`} onClick={() => setCatFilter("all")}>All</button>
            {categories.map(c => (
              <button key={c} className={`px-2 py-0.5 rounded text-xs ${catFilter === c ? "bg-slate-800 text-white" : "text-slate-500 hover:bg-slate-100"}`} onClick={() => setCatFilter(c)}>{c}</button>
            ))}
          </div>
          <div className="space-y-1 max-h-[350px] overflow-y-auto pr-1">
            {filteredCatalog.map(piece => (
              <button key={piece.id} onClick={() => addPiece(piece)} className="w-full flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 text-left text-xs" data-testid={`add-${piece.id}`}>
                <div className="w-5 h-5 rounded shrink-0" style={{ backgroundColor: piece.color + "30", border: `2px solid ${piece.color}` }} />
                <span className="text-slate-700 truncate">{piece.label}</span>
              </button>
            ))}
          </div>
          <div className="border-t pt-2">
            <p className="text-xs font-medium text-slate-500 mb-1">Custom Item</p>
            <div className="flex gap-1">
              <Input value={customLabel} onChange={(e) => setCustomLabel(e.target.value)} placeholder="Label..." className="h-7 text-xs" onKeyDown={(e) => { if (e.key === "Enter") addCustomPiece(); }} />
              <Button variant="outline" size="sm" className="h-7 text-xs shrink-0" onClick={addCustomPiece}>+</Button>
            </div>
          </div>
          {walls.length > 0 && (
            <div className="border-t pt-2">
              <p className="text-xs font-medium text-slate-500 mb-1">Walls ({walls.length})</p>
              <div className="space-y-1 max-h-24 overflow-y-auto">
                {walls.map((w, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2 py-1">
                    <span>{formatInches(wallLength(w))}</span>
                    <button onClick={() => deleteWall(i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Canvas */}
        <div className="flex-1 border rounded-lg overflow-auto bg-white" style={{ maxHeight: "550px" }}>
          <svg
            ref={canvasRef}
            width={gridW * S} height={gridH * S}
            style={{ transform: `scale(${zoom / 100})`, transformOrigin: "top left", cursor: tool === "wall" ? "crosshair" : "default" }}
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleCanvasMouseMove}
            onMouseUp={handleCanvasMouseUp}
            onMouseLeave={() => { setDragging(null); setResizing(null); setDrawingWall(null); }}
            onTouchStart={handleCanvasMouseDown}
            onTouchMove={handleCanvasMouseMove}
            onTouchEnd={handleCanvasMouseUp}
            data-testid="design-canvas"
          >
            {/* Grid */}
            <defs>
              <pattern id="smallGrid" width={12 * S} height={12 * S} patternUnits="userSpaceOnUse">
                <path d={`M ${12 * S} 0 L 0 0 0 ${12 * S}`} fill="none" stroke="#f1f5f9" strokeWidth="0.5" />
              </pattern>
              <pattern id="bigGrid" width={60 * S} height={60 * S} patternUnits="userSpaceOnUse">
                <rect width={60 * S} height={60 * S} fill="url(#smallGrid)" />
                <path d={`M ${60 * S} 0 L 0 0 0 ${60 * S}`} fill="none" stroke="#e2e8f0" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#bigGrid)" />

            {/* Scale labels */}
            {[0, 5, 10, 15, 20].map(ft => (
              <text key={`rx${ft}`} x={ft * 12 * S} y={10} fontSize="9" fill="#94a3b8">{ft}'</text>
            ))}
            {[0, 5, 10, 15].map(ft => (
              <text key={`ry${ft}`} x={2} y={ft * 12 * S + 10} fontSize="9" fill="#94a3b8">{ft}'</text>
            ))}

            {/* Walls */}
            {walls.map((w, i) => (
              <g key={`wall-${i}`}>
                <line x1={w.x1 * S} y1={w.y1 * S} x2={w.x2 * S} y2={w.y2 * S} stroke="#1e293b" strokeWidth={WALL_THICKNESS * S / 2} strokeLinecap="round" />
                {showMeasurements && (
                  <text
                    x={(w.x1 + w.x2) / 2 * S}
                    y={(w.y1 + w.y2) / 2 * S - 8}
                    fontSize="10" fill="#6366f1" fontWeight="600" textAnchor="middle"
                  >
                    {formatInches(wallLength(w))}
                  </text>
                )}
              </g>
            ))}

            {/* Wall being drawn */}
            {drawingWall && (
              <g>
                <line x1={drawingWall.x1 * S} y1={drawingWall.y1 * S} x2={mousePos.x * S} y2={mousePos.y * S} stroke="#6366f1" strokeWidth="3" strokeDasharray="6 3" />
                <text x={mousePos.x * S + 8} y={mousePos.y * S - 8} fontSize="10" fill="#6366f1">
                  {formatInches(Math.round(Math.sqrt((mousePos.x - drawingWall.x1) ** 2 + (mousePos.y - drawingWall.y1) ** 2)))}
                </text>
              </g>
            )}

            {/* Placed pieces */}
            {pieces.map(piece => {
              const isSelected = selectedPiece === piece.uid;
              return (
                <g key={piece.uid} data-testid={`piece-${piece.uid}`}>
                  <rect
                    x={piece.x * S} y={piece.y * S} width={piece.w * S} height={piece.h * S}
                    fill={piece.color} rx="3" ry="3"
                    opacity={dragging === piece.uid ? 0.6 : 0.85}
                    stroke={isSelected ? "#4f46e5" : "rgba(0,0,0,0.15)"} strokeWidth={isSelected ? 2 : 1}
                    style={{ cursor: tool === "select" ? "grab" : "default" }}
                    onMouseDown={(e) => handlePieceMouseDown(e, piece.uid)}
                    onTouchStart={(e) => { e.stopPropagation(); handlePieceMouseDown(e, piece.uid); }}
                  />
                  <text x={(piece.x + piece.w / 2) * S} y={(piece.y + piece.h / 2) * S - 2} textAnchor="middle" fontSize="8" fill="white" fontWeight="600" pointerEvents="none">
                    {piece.label}
                  </text>
                  <text x={(piece.x + piece.w / 2) * S} y={(piece.y + piece.h / 2) * S + 8} textAnchor="middle" fontSize="7" fill="rgba(255,255,255,0.7)" pointerEvents="none">
                    {piece.w}" x {piece.h}"
                  </text>
                  {/* Measurement labels on piece edges */}
                  {isSelected && showMeasurements && (
                    <>
                      <text x={(piece.x + piece.w / 2) * S} y={piece.y * S - 4} textAnchor="middle" fontSize="9" fill="#6366f1" fontWeight="bold">{piece.w}"</text>
                      <text x={(piece.x + piece.w) * S + 4} y={(piece.y + piece.h / 2) * S} fontSize="9" fill="#6366f1" fontWeight="bold" dominantBaseline="middle">{piece.h}"</text>
                    </>
                  )}
                  {/* Controls */}
                  {isSelected && tool === "select" && (
                    <>
                      <rect x={(piece.x + piece.w) * S - 5} y={(piece.y + piece.h) * S - 5} width="10" height="10" fill="white" stroke="#4f46e5" strokeWidth="2" rx="2"
                        style={{ cursor: "se-resize" }}
                        onMouseDown={(e) => { e.stopPropagation(); setResizing(piece.uid); }}
                      />
                      <g style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); rotatePiece(piece.uid); }}>
                        <circle cx={(piece.x + piece.w) * S - 8} cy={piece.y * S - 8} r="8" fill="white" stroke="#4f46e5" strokeWidth="1.5" />
                        <text x={(piece.x + piece.w) * S - 8} y={piece.y * S - 5} textAnchor="middle" fontSize="9" fill="#4f46e5">R</text>
                      </g>
                      <g style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); deletePiece(piece.uid); }}>
                        <circle cx={piece.x * S + 8} cy={piece.y * S - 8} r="8" fill="white" stroke="#ef4444" strokeWidth="1.5" />
                        <text x={piece.x * S + 8} y={piece.y * S - 5} textAnchor="middle" fontSize="9" fill="#ef4444">X</text>
                      </g>
                    </>
                  )}
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      {/* Selected piece info bar */}
      {selectedPiece && (() => {
        const p = pieces.find(pp => pp.uid === selectedPiece);
        if (!p) return null;
        return (
          <div className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg text-sm">
            <Badge style={{ backgroundColor: p.color }} className="text-white">{p.label}</Badge>
            <div className="flex items-center gap-2">
              <span className="text-slate-500">W:</span>
              <Input className="w-16 h-7 text-xs" type="number" value={p.w} onChange={(e) => updatePiece(p.uid, { w: parseInt(e.target.value) || 6 })} />
              <span className="text-slate-500">D:</span>
              <Input className="w-16 h-7 text-xs" type="number" value={p.h} onChange={(e) => updatePiece(p.uid, { h: parseInt(e.target.value) || 6 })} />
              <span className="text-xs text-slate-400">(inches)</span>
            </div>
            <Button variant="ghost" size="sm" className="h-7" onClick={() => rotatePiece(p.uid)}><RotateCw className="w-3 h-3" /></Button>
            <Button variant="ghost" size="sm" className="ml-auto text-red-500 h-7" onClick={() => deletePiece(p.uid)}><Trash2 className="w-3 h-3 mr-1" />Remove</Button>
          </div>
        );
      })()}

      <p className="text-xs text-slate-400 text-center">
        {tool === "wall" ? "Click and drag to draw a wall. Walls auto-snap to horizontal/vertical." : "Click a piece to add. Drag to move. Pieces snap to walls. Select → resize/rotate/delete."}
      </p>
    </div>
  );
}
